import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import { ProductCard } from '@/components/shop/ProductCard';
import { useProduct } from '@/hooks/useProducts';
import { useIsMobile } from '@/hooks/use-mobile';

// Curated bestseller handles
const BESTSELLER_HANDLES = [
  'collana-grigri-plus',
  'spilla-teschio-cervo',
  'collana-testa-di-cavallo-puro-sangue-arabo-copia',
  'orecchini-rinvio',
  'collana-corna-di-capriolo-rami-abete-bacche',
  'collana-testa-di-cavallo-copia',
];

export function BestsellersSection() {
  const { t } = useTranslation();

  const q0 = useProduct(BESTSELLER_HANDLES[0]);
  const q1 = useProduct(BESTSELLER_HANDLES[1]);
  const q2 = useProduct(BESTSELLER_HANDLES[2]);
  const q3 = useProduct(BESTSELLER_HANDLES[3]);
  const q4 = useProduct(BESTSELLER_HANDLES[4]);
  const q5 = useProduct(BESTSELLER_HANDLES[5]);

  const isLoading = q0.isLoading || q1.isLoading || q2.isLoading || q3.isLoading || q4.isLoading || q5.isLoading;

  const products = useMemo(() => {
    const results = [q0.data, q1.data, q2.data, q3.data, q4.data, q5.data]
      .filter(Boolean)
      .map(node => ({ node: node! }));
    return results.length > 0 ? results : undefined;
  }, [q0.data, q1.data, q2.data, q3.data, q4.data, q5.data]);

  const isMobile = useIsMobile();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    loop: true,
    slidesToScroll: isMobile ? 1 : 3,
  });

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  return (
    <section className="py-10 md:py-14 bg-secondary/50">
      <div className="container mx-auto px-4 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex items-end justify-between mb-10"
        >
          <div>
            <span className="font-body text-xs md:text-sm uppercase tracking-[0.3em] text-bronze mb-3 block">
              {t('featured.bestSeller')}
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground">
              {t('featured.artisanFavorites')}
            </h2>
          </div>
          <Link 
            to="/shop" 
            className="hidden md:flex items-center gap-2 font-body text-sm text-muted-foreground hover:text-bronze transition-colors group"
          >
            {t('featured.viewAll')}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-bronze" />
          </div>
        ) : products && products.length > 0 ? (
          <div className="relative">
            <div className="overflow-hidden" ref={emblaRef}>
              <div className="flex gap-6">
                {products.map((product, index) => (
                  <div
                    key={product.node.id}
                    className={isMobile ? 'flex-none basis-[85%]' : 'flex-none basis-[calc(33.333%-16px)]'}
                  >
                    <ProductCard product={product} index={index} />
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation arrows – desktop only */}
            {!isMobile && products.length > 3 && (
              <>
                <button
                  onClick={scrollPrev}
                  className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background/90 border border-border shadow-md flex items-center justify-center hover:bg-background transition-colors"
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-5 h-5 text-foreground" />
                </button>
                <button
                  onClick={scrollNext}
                  className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background/90 border border-border shadow-md flex items-center justify-center hover:bg-background transition-colors"
                  aria-label="Next"
                >
                  <ChevronRight className="w-5 h-5 text-foreground" />
                </button>
              </>
            )}

            {isMobile && (
              <p className="text-center text-sm text-muted-foreground mt-4">
                {t('featured.swipeToSee')}
              </p>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground">{t('featured.noProducts')}</p>
          </div>
        )}

        <Link 
          to="/shop" 
          className="flex md:hidden items-center justify-center gap-2 font-body text-sm text-muted-foreground hover:text-bronze transition-colors mt-8"
        >
          {t('featured.viewAll')}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}
